/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { generate as generateUniqueId } from 'randomstring'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { HeadCell, HeadValue } from '../HeadCell'
import ConfirmationModal from '../../Modals/ConfirmationModal'
import * as classes from './styles'

export const getEmptyColumn = (): HeadValue => ({ text: '', columnId: generateUniqueId() })

export type HeadProps = {
  columns: HeadValue[]
  onChange: (columns: HeadValue[]) => void
  isEditable: boolean
  onIsEditableChange: (isEditable: boolean) => void
  isSortable?: boolean
  maxColumnsCount?: number
}

export const Head = ({
  columns,
  onChange,
  isEditable,
  onIsEditableChange,
  isSortable = false,
  maxColumnsCount,
}: HeadProps) => {
  const [tempColumns, setTempColumns] = useState(columns)
  const [columnToRemove, setColumnToRemove] = useState<(HeadValue & { index: number }) | null>(null)

  useEffect(() => {
    setTempColumns(columns)
  }, [columns.length])

  const handleChangeTempColumn = useCallback((column: HeadValue) => {
    setTempColumns((prevColumns) =>
      prevColumns.map((prevColumn) => {
        if (prevColumn.columnId === column.columnId) {
          return column
        }

        return prevColumn
      })
    )
  }, [])

  const handleAdd = useCallback(() => {
    setTempColumns((prevColumns) => [...prevColumns, getEmptyColumn()])
  }, [])

  const handleRemove = useCallback(
    (column: HeadValue) => {
      setColumnToRemove({
        ...column,
        index: tempColumns.findIndex(({ columnId }) => columnId === column.columnId),
      })
    },
    [tempColumns]
  )

  const handleConfirmRemove = useCallback(() => {
    if (columnToRemove !== null) {
      setTempColumns((prevColumns) => prevColumns.filter(({ columnId }) => columnId !== columnToRemove.columnId))
      setColumnToRemove(null)
    }
  }, [columnToRemove])

  const handleCancel = useCallback(() => {
    setTempColumns(columns)
    onIsEditableChange(false)
  }, [columns, onIsEditableChange])

  const handleConfirm = useCallback(() => {
    onChange(tempColumns)
    onIsEditableChange(false)
  }, [tempColumns, onChange, onIsEditableChange])

  return (
    <div css={classes.head} data-testid="head-row">
      <div css={classes.columns(tempColumns.length)}>
        {tempColumns.map((value, index) => (
          <HeadCell
            value={value}
            onChange={handleChangeTempColumn}
            onRemove={handleRemove}
            isEditable={isEditable}
            key={index}
          />
        ))}
      </div>
      <div css={classes.buttons(isEditable)}>
        {!isEditable && (
          <Button
            variant="outlined"
            color="gray"
            size="small"
            onClick={() => onIsEditableChange(true)}
            css={classes.iconButton}
            data-testid="edit-head-item-button"
          >
            <EditIcon />
          </Button>
        )}
        {isEditable && (
          <>
            <Button
              variant="outlined"
              color="blue"
              size="small"
              onClick={handleAdd}
              css={classes.iconButton}
              disabled={maxColumnsCount !== undefined && tempColumns.length >= maxColumnsCount}
              data-testid="add-head-item-button"
            >
              <AddRoundedIcon />
            </Button>
            <Button
              variant="outlined"
              color="red"
              size="small"
              onClick={handleCancel}
              css={classes.iconButton}
              data-testid="cross-head-item-button"
            >
              <CrossIcon />
            </Button>
            <Button
              variant="outlined"
              color="green"
              size="small"
              onClick={handleConfirm}
              css={classes.iconButton}
              data-testid="check-head-item-button"
            >
              <CheckIcon />
            </Button>
          </>
        )}
      </div>
      {isSortable && <div css={classes.dndSpace} />}
      <ConfirmationModal
        open={columnToRemove !== null}
        title={`Deleting column #${(columnToRemove?.index ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this column?`}
        onClose={() => setColumnToRemove(null)}
        onConfirm={handleConfirmRemove}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}
